import React, { useState } from 'react';
import { generateLoveTaskAnalysis } from '../services/geminiService';
import { LoveTaskAnalysis } from '../types';
import { Heart, RefreshCw, Users, Shield } from 'lucide-react';
import { useUser } from '../context/UserContext';

const HappyLove: React.FC = () => {
  const [context, setContext] = useState('');
  const [analysis, setAnalysis] = useState<LoveTaskAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const { addXp } = useUser();

  const handleAnalyze = async () => {
    if (!context.trim()) return;
    setLoading(true);
    setAnalysis(null);
    try {
      const data = await generateLoveTaskAnalysis(context);
      setAnalysis(data);
      addXp(40, 'Analyzed Task of Love');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-pink-50 text-pink-600 rounded-full mb-4">
          <Heart size={32} />
        </div>
        <h1 className="text-3xl font-serif font-bold text-nunchi-900 mb-2">The Task of Love</h1>
        <p className="text-nunchi-600">
          Adler teaches that love is not a feeling, but a task. <br/>
          It is the shift from "Me" (Ego) to "Us" (Community).
        </p>
      </header>

      {!analysis && !loading && (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-nunchi-200">
            <label className="block text-sm font-bold text-nunchi-500 uppercase tracking-wider mb-3">
                Describe a relationship struggle (Partner, Friend, Family)
            </label>
            <textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="e.g., I feel like I give more than I get, I am afraid of commitment, I feel judged by my parents..."
              className="w-full h-32 bg-nunchi-50 rounded-xl p-4 border-none focus:ring-2 focus:ring-pink-200 outline-none resize-none text-nunchi-800 placeholder:text-nunchi-300 mb-4"
            />
            <button
              onClick={handleAnalyze}
              disabled={!context.trim()}
              className="w-full bg-pink-700 text-white py-4 rounded-xl font-bold hover:bg-pink-800 transition-all flex justify-center items-center gap-2 disabled:opacity-50"
            >
              <Heart size={18} /> Analyze My Love Task
            </button>
        </div>
      )}

      {loading && (
        <div className="text-center py-20">
             <div className="flex justify-center mb-4 text-pink-300 animate-pulse">
                <Heart size={48} />
             </div>
             <p className="font-serif italic text-pink-700">Shifting the subject to "Us"...</p>
        </div>
      )}

      {analysis && (
        <div className="animate-fadeIn space-y-6">
            <div className="bg-pink-50 p-6 rounded-xl border border-pink-100">
                <h3 className="text-pink-700 font-bold uppercase tracking-wider text-xs mb-2 flex items-center gap-2">
                    <Users size={14} /> The Focus Shift
                </h3>
                <p className="text-nunchi-800 font-medium leading-relaxed">{analysis.focusShift}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-6 rounded-xl border border-nunchi-200 shadow-sm">
                    <h3 className="text-nunchi-400 font-bold uppercase tracking-wider text-xs mb-2">Diagnosis</h3>
                    <p className="text-nunchi-700 text-sm">{analysis.diagnosis}</p>
                </div>
                 <div className="bg-white p-6 rounded-xl border border-nunchi-200 shadow-sm">
                    <h3 className="text-nunchi-400 font-bold uppercase tracking-wider text-xs mb-2">Courageous Advice</h3>
                    <p className="text-nunchi-700 text-sm font-medium">{analysis.advice}</p>
                </div>
            </div>

            <div className="text-center">
                <button 
                  onClick={() => { setAnalysis(null); setContext(''); }}
                  className="text-pink-600 hover:text-pink-800 font-bold flex items-center gap-2 mx-auto"
                >
                    <RefreshCw size={16} /> Analyze Another Task
                </button>
            </div>
        </div>
      )}
    </div>
  );
};

export default HappyLove;